import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { UserAuthActions } from './user-auth.actions';
import { AuthService } from '../../../core/services/auth/auth.service';
import { GhUserStatus } from '../../../features/administration/users.models';
import { selectUserAuthLoadId } from './user-auth.selectors';

@Injectable()
export class UserAuthEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private authService = inject(AuthService);

  // GET /api/me/profile  (via administrationConfig)
  loadUserDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserAuthActions.loadUserDetails),
      switchMap(() => {
        // Captured after the reducer bumps loadId. Refresh starts this from the
        // route guard, and a 403 can start it again; a slower older response
        // must not replace the privileges the UI already settled on.
        const loadId = this.currentLoadId();
        return this.authService.getUserProfile().pipe(
          switchMap((response) => {
            if (this.currentLoadId() !== loadId) return EMPTY;
            if (response.body) {
              const userStatus = response.body.data?.[0]?.status;
              if (userStatus === GhUserStatus.Inactive) {
                return of(
                  UserAuthActions.accountDeactivated({
                    message: 'Your account has been deactivated. Please contact your administrator.',
                  })
                );
              }
              return of(UserAuthActions.loadUserDetailsSuccess({ response: response.body }));
            }
            return of(UserAuthActions.loadUserDetailsFailure({ error: 'Response body is empty' }));
          }),
          catchError((error) => {
            if (this.currentLoadId() !== loadId) return EMPTY;
            if (error?.status === 403) {
              return of(
                UserAuthActions.accountDeactivated({
                  message: error?.error?.message || 'Access Denied',
                })
              );
            }
            return of(
              UserAuthActions.loadUserDetailsFailure({
                error: error?.error?.message || error?.message || 'Failed to load user details',
              })
            );
          })
        );
      })
    )
  );

  private currentLoadId(): number {
    let loadId = 0;
    this.store
      .select(selectUserAuthLoadId)
      .pipe(take(1))
      .subscribe((id) => {
        loadId = id;
      });
    return loadId;
  }

}
